import React, { useEffect, useRef, useState } from 'react';

import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-quality-levels';
import videojsqualityselector from 'videojs-hls-quality-selector';

import { VideoTranscription } from 'components/VideoTranscription';
import { useCaptionsURL } from 'hooks/useCaptionsUrl';
import { MuxDataOptions } from 'typings/videojs-mux';
import { useFeatureFlagContext } from 'utils/unleashFeatureFlags/FeatureFlagProvider';
import { FEATURE_FLAGS } from 'utils/unleashFeatureFlags/flags';

import { useInitMux, useVideoTracking } from './hooks';

import getConfig from 'next/config';
import { useRouter } from 'next/router';

import useSWR from 'swr';
import axios from 'axios';

type Props = {
  videoSrc: string;
  muxMetaData: Omit<
    MuxDataOptions,
    'video_duration' | 'player_init_time' | 'env_key' | 'video_encoding_variant'
  >;
  videoJsOptions?: VideoJsPlayerOptions;
  containerStyles?: React.CSSProperties;
  onEvent?: Partial<{
    play: (event: Event) => void;
  }>;
  logWatchTime?: (props: { watchedFor: number }) => void;
  fluid?: boolean;
  pictureInPictureToggle?: boolean;
  organizationId?: string;
};

const VideoPlayer = React.forwardRef<HTMLVideoElement, Props>((props, ref) => {
  const {
    videoSrc,
    videoJsOptions,
    muxMetaData,
    containerStyles = {},
    onEvent = {},
    fluid = true,
    pictureInPictureToggle = true,
  } = props;
  const [player, setPlayer] = useState<VideoJsPlayer>();
  const newVidRef = useRef<HTMLVideoElement>(null);
  const playerRef = (ref as React.RefObject<HTMLVideoElement>) ?? newVidRef;
  const { publicRuntimeConfig } = getConfig();

  // reference handlers so that we don't have to rerun the hook below
  const eventRef = useRef<typeof onEvent>({});
  const { features } = useFeatureFlagContext();
  const { query } = useRouter();
  const transcriptionsFlag = features[FEATURE_FLAGS.ENTERPRISE_TRANSCRIPTIONS];

  const { captionsUrl, loading } = useCaptionsURL(props.organizationId);

  // Mux init
  useInitMux(playerRef.current, muxMetaData);

  useEffect(() => {
    eventRef.current = props.onEvent ?? {};
  }, [props.onEvent]);

  useEffect(() => {
    if (playerRef.current && !(loading && transcriptionsFlag)) {
      const playerjs = videojs(playerRef.current, {
        controls: true,
        fluid,
        controlBar: {
          pictureInPictureToggle: true,
        },
        textTrackSettings: { persistTextTrackSettings: true },
        tracks: captionsUrl
          ? [
              {
                src: captionsUrl,
                kind: 'captions',
                srclang: 'en',
                label: 'English',
                mode: 'showing',
              },
            ]
          : undefined,
        ...videoJsOptions,
      });
      setPlayer(playerjs);
    }
    const { play } = eventRef.current;
    const videoEl = playerRef.current;

    if (play && videoEl) videoEl.addEventListener('play', play);

    return () => {
      if (play && videoEl) videoEl.removeEventListener('play', play);
    };
  }, [videoJsOptions, playerRef, fluid, pictureInPictureToggle, captionsUrl, loading]);

  useVideoTracking(playerRef.current, props.logWatchTime ?? (() => null));

  useEffect(() => {
    if (!player) return;
    player.ready(() => {
      player.src(videoSrc);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const qualityLevels = player.qualityLevels();
      qualityLevels.on('addqualitylevel', event => {
        const { qualityLevel } = event;
        qualityLevel.enabled = qualityLevel.height >= 720;
      });

      player.hlsQualitySelector = videojsqualityselector;

      player.hlsQualitySelector({
        displayCurrentQuality: true,
      });
    });
  }, [player, videoSrc]);

  return (
    <>
      <div data-vjs-player style={containerStyles}>
        <video ref={playerRef} className="video-js vjs-big-play-centered" />
      </div>
      {props.organizationId && (
        <VideoTranscription
          videoElement={playerRef.current}
          organizationId={props.organizationId}
        />
      )}
    </>
  );
});

export default VideoPlayer;
